import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash, PencilSimple, Wrench, Package, FolderSimple, MagnifyingGlass, Warning, HandArrowDown, ArrowUUpLeft, ArrowsLeftRight } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { fmtDate } from "@/lib/constants";
import ThirdPartyPanel from "@/components/ThirdPartyPanel";

const STATUS = {
  disponibil: { label: "Disponibil", cls: "bg-emerald-500/15 text-emerald-400" },
  alocat: { label: "Alocat", cls: "bg-blue-500/15 text-blue-400" },
  in_reparatie: { label: "În reparație", cls: "bg-amber-500/15 text-amber-400" },
  inchiriat: { label: "Închiriat", cls: "bg-violet-500/15 text-violet-300" },
  casat: { label: "Casat", cls: "bg-red-500/15 text-red-400" },
};

const SCOPES = [
  { value: "general", label: "Gestiune Generală", icon: Package },
  { value: "project", label: "Gestiune Proiect", icon: FolderSimple },
  { value: "third_party", label: "Gestiune Terți", icon: ArrowsLeftRight },
  { value: "tool", label: "Gestiune Scule", icon: Wrench },
];

const empty = { name: "", code: "", category: "", quantity: 1, unit: "buc", unit_price: 0, min_stock: 0, location: "", holder: "", status: "disponibil", source: "", supplier: "", notes: "" };

function ItemDialog({ open, onClose, onSave, initial }) {
  const [f, setF] = useState({ ...empty, ...(initial || {}) });
  useEffect(() => { setF({ ...empty, ...(initial || {}) }); }, [initial, open]);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{initial?.id ? "Editează articol" : "Adaugă articol"}</DialogTitle></DialogHeader>
        <div className="grid max-h-[65vh] gap-3 overflow-y-auto pr-1">
          <div className="grid gap-1.5"><Label>Denumire *</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="inv-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5"><Label>Cod / Serie</Label><Input value={f.code} onChange={(e) => set("code", e.target.value)} /></div>
            <div className="grid gap-1.5"><Label>Categorie</Label><Input value={f.category} onChange={(e) => set("category", e.target.value)} /></div>
            <div className="grid gap-1.5"><Label>Cantitate</Label><Input type="number" value={f.quantity} onChange={(e) => set("quantity", e.target.value)} /></div>
            <div className="grid gap-1.5"><Label>UM</Label><Input value={f.unit} onChange={(e) => set("unit", e.target.value)} /></div>
            <div className="grid gap-1.5"><Label>Preț unitar (lei)</Label><Input type="number" value={f.unit_price} onChange={(e) => set("unit_price", e.target.value)} data-testid="inv-price" /></div>
            <div className="grid gap-1.5"><Label>Prag stoc minim</Label><Input type="number" value={f.min_stock} onChange={(e) => set("min_stock", e.target.value)} data-testid="inv-minstock" /></div>
            <div className="grid gap-1.5"><Label>Locație</Label><Input value={f.location} onChange={(e) => set("location", e.target.value)} /></div>
            <div className="grid gap-1.5"><Label>Responsabil</Label><Input value={f.holder} onChange={(e) => set("holder", e.target.value)} /></div>
            <div className="grid gap-1.5">
              <Label>Status</Label>
              <Select value={f.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="inv-status"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5"><Label>Furnizor</Label><Input value={f.supplier} onChange={(e) => set("supplier", e.target.value)} /></div>
          </div>
          <div className="grid gap-1.5"><Label>Sursă / Achiziție</Label><Input value={f.source} onChange={(e) => set("source", e.target.value)} placeholder="ex. Factură 12.02.2026" /></div>
          <div className="grid gap-1.5"><Label>Note</Label><Input value={f.notes} onChange={(e) => set("notes", e.target.value)} /></div>
        </div>
        <DialogFooter><Button onClick={() => onSave(f)} data-testid="inv-save">Salvează</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AllocateDialog({ open, onClose, item, projects, onDone }) {
  const [projectId, setProjectId] = useState("");
  const [qty, setQty] = useState(1);
  const [holder, setHolder] = useState("");
  const [notes, setNotes] = useState("");
  useEffect(() => { if (open) { setProjectId(""); setQty(1); setHolder(""); setNotes(""); } }, [open]);
  const isTool = item?.scope === "tool";

  const submit = async () => {
    if (!projectId) { toast.error("Alege un proiect"); return; }
    try {
      await api.post("/inventory/allocate", {
        source_item_id: item.id, project_id: projectId,
        quantity: isTool ? 1 : Number(qty) || 1, holder, notes,
      });
      toast.success("Alocat pe proiect");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Alocă „{item?.name}" pe proiect</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label>Proiect *</Label>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger data-testid="allocate-project"><SelectValue placeholder="Alege proiect…" /></SelectTrigger>
              <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} · {p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {!isTool && (
            <div className="grid gap-1.5"><Label>Cantitate (disp. {item?.quantity} {item?.unit})</Label>
              <Input type="number" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="allocate-qty" /></div>
          )}
          <div className="grid gap-1.5"><Label>Responsabil (șef șantier)</Label><Input value={holder} onChange={(e) => setHolder(e.target.value)} data-testid="allocate-holder" /></div>
          <div className="grid gap-1.5"><Label>Note</Label><Input value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
        </div>
        <DialogFooter><Button onClick={submit} data-testid="allocate-submit">Alocă</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InventoryTable({ scope, projectId, projects, onChanged }) {
  const { hasRole } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const [allocItem, setAllocItem] = useState(null);
  const canEdit = hasRole("pm", "inginer", "admin");
  const canAllocate = (scope === "general" || scope === "tool") && canEdit;

  const load = useCallback(() => {
    if (scope === "project" && !projectId) { setItems([]); return; }
    const params = { scope };
    if (scope === "project") params.project_id = projectId;
    api.get("/inventory", { params }).then((r) => setItems(r.data)).catch(() => {});
  }, [scope, projectId]);
  useEffect(() => { load(); }, [load]);

  const save = async (f) => {
    const body = { ...f, quantity: Number(f.quantity) || 0, unit_price: Number(f.unit_price) || 0, min_stock: Number(f.min_stock) || 0, scope };
    if (scope === "project") body.project_id = projectId;
    try {
      if (editing?.id) await api.put(`/inventory/${editing.id}`, body);
      else await api.post("/inventory", body);
      toast.success("Salvat");
      setDlg(false); setEditing(null); load(); onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/inventory/${id}`); load(); onChanged?.(); };

  const filtered = items.filter((i) =>
    !q || `${i.name} ${i.code} ${i.category} ${i.holder} ${i.source}`.toLowerCase().includes(q.toLowerCase()));

  if (scope === "project" && !projectId)
    return <p className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">Selectează un proiect pentru a vedea gestiunea alocată.</p>;

  const canAllocRow = (i) => scope === "tool" ? i.status === "disponibil" : (i.quantity || 0) > 0;

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Caută…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="inv-search" />
        </div>
        <span className="text-sm text-muted-foreground">{filtered.length} articole</span>
        {canEdit && <Button size="sm" onClick={() => { setEditing(null); setDlg(true); }} data-testid="inv-add-btn"><Plus size={16} className="mr-1" /> Adaugă</Button>}
      </div>

      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.02] text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-4 py-2.5 text-left font-semibold">Denumire</th>
              <th className="px-4 py-2.5 text-left font-semibold">Cod / Serie</th>
              <th className="px-4 py-2.5 text-left font-semibold">Categorie</th>
              <th className="px-4 py-2.5 text-right font-semibold">Cant.</th>
              <th className="px-4 py-2.5 text-right font-semibold">Preț</th>
              <th className="px-4 py-2.5 text-left font-semibold">Locație / Resp.</th>
              <th className="px-4 py-2.5 text-left font-semibold">Status</th>
              <th className="px-4 py-2.5 text-left font-semibold">Sursă</th>
              {canEdit && <th className="px-4 py-2.5 text-right font-semibold">Acțiuni</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered.map((i) => (
              <tr key={i.id} className="hover:bg-white/[0.02]" data-testid={`inv-row-${i.id}`}>
                <td className="px-4 py-2.5 font-medium">{i.name}</td>
                <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground max-w-[180px] truncate" title={i.code}>{i.code || "—"}</td>
                <td className="px-4 py-2.5 text-muted-foreground">{i.category || "—"}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{i.quantity} {i.unit}</td>
                <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground">{i.unit_price ? `${i.unit_price} lei` : "—"}</td>
                <td className="px-4 py-2.5 text-muted-foreground">{[i.location, i.holder].filter(Boolean).join(" · ") || "—"}</td>
                <td className="px-4 py-2.5"><span className={cn("rounded px-1.5 py-0.5 text-[11px] font-medium", STATUS[i.status]?.cls)}>{STATUS[i.status]?.label || i.status}</span></td>
                <td className="px-4 py-2.5 text-xs text-muted-foreground">{i.source || "—"}</td>
                {canEdit && (
                  <td className="px-4 py-2.5 text-right">
                    <div className="flex justify-end gap-1.5">
                      {canAllocate && canAllocRow(i) && (
                        <button onClick={() => setAllocItem(i)} className="text-blue-400 hover:text-blue-300" title="Alocă pe proiect" data-testid={`inv-alloc-${i.id}`}><HandArrowDown size={16} /></button>
                      )}
                      <button onClick={() => { setEditing(i); setDlg(true); }} className="text-muted-foreground hover:text-foreground" data-testid={`inv-edit-${i.id}`}><PencilSimple size={15} /></button>
                      <button onClick={() => del(i.id)} className="text-muted-foreground hover:text-red-400" data-testid={`inv-del-${i.id}`}><Trash size={15} /></button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">Niciun articol.</td></tr>}
          </tbody>
        </table>
      </div>

      <ItemDialog open={dlg} onClose={() => { setDlg(false); setEditing(null); }} onSave={save} initial={editing} />
      <AllocateDialog open={!!allocItem} onClose={() => setAllocItem(null)} item={allocItem} projects={projects}
        onDone={() => { setAllocItem(null); load(); onChanged?.(); }} />
    </div>
  );
}

function AllocationsHistory({ projectId, refreshKey, onChanged }) {
  const { hasRole } = useAuth();
  const [allocs, setAllocs] = useState([]);
  const canEdit = hasRole("pm", "inginer", "admin");
  const load = useCallback(() => {
    if (!projectId) { setAllocs([]); return; }
    api.get("/inventory/allocations", { params: { project_id: projectId } }).then((r) => setAllocs(r.data)).catch(() => {});
  }, [projectId, refreshKey]);
  useEffect(() => { load(); }, [load]);

  const doReturn = async (aid) => {
    try { await api.post(`/inventory/allocations/${aid}/return`); toast.success("Returnat în gestiune"); load(); onChanged?.(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!projectId) return null;
  return (
    <div className="mt-6" data-testid="allocations-history">
      <h3 className="mb-2 font-head text-lg font-bold">Istoric alocări scule / materiale</h3>
      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.02] text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-4 py-2.5 text-left font-semibold">Articol</th>
              <th className="px-4 py-2.5 text-left font-semibold">Sursă</th>
              <th className="px-4 py-2.5 text-right font-semibold">Cant.</th>
              <th className="px-4 py-2.5 text-left font-semibold">Responsabil</th>
              <th className="px-4 py-2.5 text-left font-semibold">Alocat</th>
              <th className="px-4 py-2.5 text-left font-semibold">Status</th>
              {canEdit && <th className="px-4 py-2.5"></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {allocs.map((a) => (
              <tr key={a.id} data-testid={`alloc-row-${a.id}`}>
                <td className="px-4 py-2.5 font-medium">{a.source_name}</td>
                <td className="px-4 py-2.5 text-muted-foreground">{a.source_scope === "tool" ? "Scule" : "Generală"}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{a.quantity} {a.unit}</td>
                <td className="px-4 py-2.5 text-muted-foreground">{a.holder || "—"}</td>
                <td className="px-4 py-2.5 text-xs text-muted-foreground">{fmtDate(a.allocated_at)} · {a.allocated_by_name || ""}</td>
                <td className="px-4 py-2.5"><span className={cn("rounded px-1.5 py-0.5 text-[11px]", a.status === "returnat" ? "bg-white/5 text-muted-foreground" : "bg-blue-500/15 text-blue-400")}>{a.status === "returnat" ? "Returnat" : "Alocat"}</span></td>
                {canEdit && <td className="px-4 py-2.5 text-right">{a.status !== "returnat" && <button onClick={() => doReturn(a.id)} className="inline-flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300" data-testid={`alloc-return-${a.id}`}><ArrowUUpLeft size={14} /> Returnează</button>}</td>}
              </tr>
            ))}
            {allocs.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Nicio alocare.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AlertsBanner({ alerts }) {
  if (!alerts || alerts.count === 0) return null;
  const items = [...alerts.unavailable_tools, ...alerts.out_of_stock];
  return (
    <div className="mb-5 rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-4" data-testid="inventory-alerts">
      <div className="flex items-center gap-2 text-amber-400">
        <Warning size={18} weight="fill" />
        <span className="font-semibold">{alerts.count} echipament(e) indisponibil(e)</span>
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        {items.slice(0, 12).map((i) => (
          <span key={i.id} className="rounded-md bg-black/30 px-2 py-1 text-xs text-muted-foreground">
            {i.name} · <span className={STATUS[i.status]?.cls?.replace(/bg-[^ ]+/, "")}>{STATUS[i.status]?.label || (i.scope === "general" ? "Stoc 0" : i.status)}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

export default function Inventory() {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [alerts, setAlerts] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadAlerts = useCallback(() => api.get("/inventory/alerts").then((r) => setAlerts(r.data)).catch(() => {}), []);
  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    loadAlerts();
  }, [loadAlerts]);

  const onChanged = () => { loadAlerts(); setRefreshKey((k) => k + 1); };

  return (
    <div className="mx-auto max-w-6xl px-6 py-8 fade-up" data-testid="inventory-page">
      <div className="mb-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Active & Stocuri</p>
        <h1 className="font-head text-3xl font-extrabold tracking-tighter">Gestiune</h1>
      </div>

      <AlertsBanner alerts={alerts} />

      <Tabs defaultValue="general">
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1">
          {SCOPES.map((s) => (
            <TabsTrigger key={s.value} value={s.value} data-testid={`inv-tab-${s.value}`}>
              <s.icon size={16} className="mr-1.5" /> {s.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="general" className="mt-4"><InventoryTable scope="general" projects={projects} onChanged={onChanged} /></TabsContent>

        <TabsContent value="project" className="mt-4">
          <div className="mb-4 max-w-sm">
            <Label className="mb-1.5 block text-xs text-muted-foreground">Proiect</Label>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger data-testid="inv-project-select"><SelectValue placeholder="Alege proiect…" /></SelectTrigger>
              <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} · {p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <InventoryTable scope="project" projectId={projectId} projects={projects} onChanged={onChanged} />
          <AllocationsHistory projectId={projectId} refreshKey={refreshKey} onChanged={onChanged} />
        </TabsContent>

        <TabsContent value="third_party" className="mt-4"><ThirdPartyPanel projects={projects} onChanged={onChanged} /></TabsContent>

        <TabsContent value="tool" className="mt-4"><InventoryTable scope="tool" projects={projects} onChanged={onChanged} /></TabsContent>
      </Tabs>
    </div>
  );
}
